import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function matchesSearch(c, query) {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  const recipientText = `${c.recipient?.first_name || ''} ${c.recipient?.last_name || ''} ${c.recipient?.affiliate_code || ''}`.toLowerCase();
  const referredText = `${c.referred?.first_name || ''} ${c.referred?.last_name || ''} ${c.referred?.affiliate_code || ''}`.toLowerCase();
  return recipientText.includes(q) || referredText.includes(q) || (c.package_code || '').toLowerCase().includes(q);
}

export default function DirectReferral() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [statusMessage, setStatusMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [exclusions, setExclusions] = useState([]);
  const [exclusionsLoading, setExclusionsLoading] = useState(true);
  const [excludingId, setExcludingId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  const fetchCommissions = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/direct-referral/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load commissions');
      }
      setCommissions(data.commissions || []);
      setSelectedIds({});
    } catch (err) {
      setError(err.message || 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchExclusions = async () => {
    setExclusionsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/direct-referral/exclusions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load exclusions');
      }
      setExclusions(data.exclusions || []);
    } catch (err) {
      setError(err.message || 'Failed to load exclusions');
    } finally {
      setExclusionsLoading(false);
    }
  };

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    fetchCommissions();
    fetchExclusions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleCalculate = async () => {
    setCalculating(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/direct-referral/calculate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to calculate commissions');
      }
      setStatusMessage(isZh ? `发现 ${data.commissions_created} 笔新佣金。` : `Found ${data.commissions_created} new commission${data.commissions_created === 1 ? '' : 's'}.`);
      await fetchCommissions();
    } catch (err) {
      setError(err.message || 'Failed to calculate commissions');
    } finally {
      setCalculating(false);
    }
  };

  const handleRelease = async (ids) => {
    if (!ids || ids.length === 0) return;
    setReleasing(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/direct-referral/release`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ commission_ids: ids }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to release commissions');
      }
      const failures = (data.results || []).filter((r) => !r.success);
      const successes = (data.results || []).filter((r) => r.success);
      if (failures.length > 0) {
        setStatusMessage(isZh
          ? `已发放 ${successes.length} 笔，${failures.length} 笔失败：${failures.map((f) => f.message).join('; ')}`
          : `Released ${successes.length}, ${failures.length} failed: ${failures.map((f) => f.message).join('; ')}`);
      } else {
        setStatusMessage(isZh ? `已发放 ${successes.length} 笔佣金。` : `Released ${successes.length} commission${successes.length === 1 ? '' : 's'}.`);
      }
      await fetchCommissions();
    } catch (err) {
      setError(err.message || tt('Failed to release commissions', '发放佣金失败'));
    } finally {
      setReleasing(false);
    }
  };

  const handleExclude = async (commissionId) => {
    const reason = window.prompt(tt('Reason for excluding this purchase from future Direct Referral calculations (optional):', '将此笔购买从未来的直推计算中排除的原因（可选）：'), '');
    if (reason === null) return; // cancelled
    setExcludingId(commissionId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/direct-referral/exclude`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ commission_id: commissionId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to exclude purchase');
      }
      setStatusMessage(data.message);
      await fetchCommissions();
      await fetchExclusions();
    } catch (err) {
      setError(err.message || 'Failed to exclude purchase');
    } finally {
      setExcludingId(null);
    }
  };

  const handleRemoveExclusion = async (exclusionId) => {
    setExcludingId(exclusionId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/direct-referral/remove-exclusion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ exclusion_id: exclusionId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove exclusion');
      }
      setStatusMessage(data.message);
      await fetchExclusions();
    } catch (err) {
      setError(err.message || 'Failed to remove exclusion');
    } finally {
      setExcludingId(null);
    }
  };

  const pendingCommissions = useMemo(
    () => commissions.filter((c) => c.status === 'pending'),
    [commissions]
  );
  const releasedCommissions = useMemo(
    () => commissions.filter((c) => c.status === 'released'),
    [commissions]
  );
  const flaggedCommissions = useMemo(
    () => commissions.filter((c) => c.status === 'flagged'),
    [commissions]
  );

  const tabCommissions = activeTab === 'pending' ? pendingCommissions
    : activeTab === 'released' ? releasedCommissions
    : flaggedCommissions;
  const displayedCommissions = useMemo(
    () => tabCommissions.filter((c) => matchesSearch(c, searchQuery)),
    [tabCommissions, searchQuery]
  );

  const allVisibleSelected = activeTab === 'pending'
    && displayedCommissions.length > 0
    && displayedCommissions.every((c) => selectedIds[c.id]);

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds({});
    } else {
      const next = {};
      displayedCommissions.forEach((c) => { next[c.id] = true; });
      setSelectedIds(next);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  const handlePrint = () => window.print();

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return null;
  }

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
    <style>{`
      @page { size: landscape; }
      @media print {
        header, nav, footer, .no-print { display: none !important; }
        html, body { background: #1a3a2a !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
      }
    `}</style>
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
          {tt('Direct Referral Commissions', '直推佣金')}
        </h1>
        <div className="flex items-center gap-3 no-print">
          <button
            type="button"
            onClick={handlePrint}
            className="px-4 py-2 rounded-lg font-medium border"
            style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
          >
            🖨️ {tt('Print', '打印')}
          </button>
          <button
            type="button"
            onClick={handleCalculate}
            disabled={calculating || !isBjoechia}
            title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {calculating ? tt('Calculating...', '计算中...') : tt('Calculate', '计算')}
          </button>
        </div>
      </div>
      {!isBjoechia && (
        <p className="text-body-xs mb-4 no-print" style={{ color: '#999999' }}>
          {tt('Only WELLIS-BJOECHIA can run Calculate or Release.', '只有 WELLIS-BJOECHIA 可以执行"计算"或"发放"操作。')}
        </p>
      )}

      {error && (
        <div className="border rounded-lg p-4 mb-4 no-print" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {error}
        </div>
      )}
      {statusMessage && (
        <div className="border rounded-lg p-4 mb-4 no-print" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
          {statusMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="no-print flex gap-2 mb-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        <button
          type="button"
          onClick={() => setActiveTab('pending')}
          className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
          style={
            activeTab === 'pending'
              ? { color: '#0d2818', backgroundColor: '#c9a961' }
              : { color: '#c9a961', backgroundColor: 'transparent' }
          }
        >
          {tt('Pending', '待发放')} ({pendingCommissions.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('released')}
          className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
          style={
            activeTab === 'released'
              ? { color: '#0d2818', backgroundColor: '#c9a961' }
              : { color: '#c9a961', backgroundColor: 'transparent' }
          }
        >
          {tt('Released', '已发放')} ({releasedCommissions.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('flagged')}
          className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
          style={
            activeTab === 'flagged'
              ? { color: '#0d2818', backgroundColor: '#c9a961' }
              : { color: '#c9a961', backgroundColor: 'transparent' }
          }
        >
          {tt('Flagged', '已标记')} ({flaggedCommissions.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('exclusions')}
          className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
          style={
            activeTab === 'exclusions'
              ? { color: '#0d2818', backgroundColor: '#c9a961' }
              : { color: '#c9a961', backgroundColor: 'transparent' }
          }
        >
          {tt('Exclusions', '排除列表')} ({exclusions.length})
        </button>
      </div>

      {activeTab === 'exclusions' ? (
        <>
          {exclusionsLoading ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading exclusions...', '正在加载排除列表...')}</p>
          ) : exclusions.length === 0 ? (
            <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt('No excluded purchases. Use the "Exclude" button on a pending or flagged commission to stop it from being recalculated in the future.', '暂无已排除的购买记录。可在待发放或已标记的佣金记录上点击"排除"按钮，使其不再被重新计算。')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2">{tt('Buyer', '买家')}</th>
                    <th className="p-2">{tt('Package', '配套')}</th>
                    <th className="p-2">{tt('Reason', '原因')}</th>
                    <th className="p-2">{tt('Excluded By', '操作人')}</th>
                    <th className="p-2">{tt('Date', '日期')}</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {exclusions.map((ex) => (
                    <tr key={ex.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                      <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(ex.buyer)}</td>
                      <td className="p-2">
                        {ex.package_code} ({Number(ex.package_price).toFixed(2)} CP)
                        {ex.source === 'pre_processed' && (
                          <span className="ml-2 px-2 py-0.5 rounded text-body-xs font-medium" style={{ backgroundColor: 'rgba(201,169,97,0.15)', color: '#c9a961' }}>
                            {tt('Pre-processed', '预处理')}
                          </span>
                        )}
                      </td>
                      <td className="p-2">{ex.reason || '—'}</td>
                      <td className="p-2">{memberLabel(ex.excluded_by)}</td>
                      <td className="p-2">{new Date(ex.created_at).toLocaleDateString()}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveExclusion(ex.id)}
                          disabled={excludingId === ex.id}
                          className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                        >
                          {excludingId === ex.id ? tt('Removing...', '移除中...') : tt('Re-include', '重新纳入')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
      <>
      {/* Search */}
      <div className="no-print mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={tt('Search by recipient, referred member, or package code', '按接收人、被推荐人或配套代码搜索')}
          className="w-full px-4 py-2 rounded-lg border text-body-sm focus:outline-none"
          style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }}
        />
      </div>

      {/* Print-only heading */}
      <p className="hidden print:block text-body-sm mb-4" style={{ color: '#c9a961' }}>
        {isZh
          ? `${activeTab === 'pending' ? '待发放' : activeTab === 'released' ? '已发放' : '已标记'}直推佣金`
          : `${activeTab === 'pending' ? 'Pending' : activeTab === 'released' ? 'Released' : 'Flagged'} Direct Referral Commissions`}
        {searchQuery.trim() ? ` — ${tt('filtered', '已筛选')}: "${searchQuery.trim()}"` : ''}
      </p>

      {activeTab === 'pending' && displayedCommissions.length > 0 && (
        <div className="flex items-center gap-3 mb-4 no-print">
          <label className="flex items-center gap-2 text-body-sm" style={{ color: '#b8a68f' }}>
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
            {tt('Select all', '全选')}
          </label>
          <button
            type="button"
            onClick={() => handleRelease(Object.keys(selectedIds).filter((id) => selectedIds[id]))}
            disabled={releasing || selectedCount === 0 || !isBjoechia}
            title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {releasing ? tt('Releasing...', '发放中...') : tt(`Release Selected (${selectedCount})`, `发放所选 (${selectedCount})`)}
          </button>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#b8a68f' }}>{tt('Loading commissions...', '正在加载佣金...')}</p>
      ) : commissions.length === 0 ? (
        <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <p style={{ color: '#b8a68f' }}>{tt('No commissions yet. Click Calculate to find pending direct referral commissions.', '暂无佣金记录。点击"计算"查找待发放的直推佣金。')}</p>
        </div>
      ) : displayedCommissions.length === 0 ? (
        <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <p style={{ color: '#b8a68f' }}>
            {searchQuery.trim()
              ? tt('No commissions match your search.', '没有符合搜索条件的佣金记录。')
              : tt(`No ${activeTab} commissions.`, `暂无${activeTab === 'pending' ? '待发放' : activeTab === 'released' ? '已发放' : '已标记'}佣金。`)}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
            <thead>
              <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                <th className="p-2 no-print"></th>
                <th className="p-2">{tt('Recipient', '接收人')}</th>
                <th className="p-2">{tt('Referred Member', '被推荐人')}</th>
                <th className="p-2">{tt('Tier', '级别')}</th>
                <th className="p-2">{tt('Package', '配套')}</th>
                <th className="p-2">{tt('Amount (CP)', '金额 (CP)')}</th>
                <th className="p-2">{tt('Status', '状态')}</th>
                <th className="p-2 no-print"></th>
              </tr>
            </thead>
            <tbody>
              {displayedCommissions.map((c) => (
                <tr key={c.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                  <td className="p-2 no-print">
                    {c.status === 'pending' && (
                      <input
                        type="checkbox"
                        checked={!!selectedIds[c.id]}
                        onChange={() => toggleSelect(c.id)}
                      />
                    )}
                  </td>
                  <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(c.recipient)}</td>
                  <td className="p-2">{memberLabel(c.referred)}</td>
                  <td className="p-2">{tt('Tier', '级别')} {c.tier} ({c.rate_percent}%)</td>
                  <td className="p-2">
                    {c.package_code} ({Number(c.package_price).toFixed(2)} CP)
                    {c.source === 'pre_processed' && (
                      <span className="ml-2 px-2 py-0.5 rounded text-body-xs font-medium" style={{ backgroundColor: 'rgba(201,169,97,0.15)', color: '#c9a961' }}>
                        {tt('Pre-processed', '预处理')}
                      </span>
                    )}
                  </td>
                  <td className="p-2">{Number(c.amount).toFixed(2)}</td>
                  <td className="p-2">
                    <span
                      className="px-2 py-1 rounded text-body-xs font-medium"
                      style={{
                        backgroundColor: c.status === 'released' ? 'rgba(201,169,97,0.2)'
                          : c.status === 'flagged' ? 'rgba(217,119,6,0.15)' : 'rgba(201,169,97,0.05)',
                        color: c.status === 'released' ? '#c9a961'
                          : c.status === 'flagged' ? '#d97706' : '#b8a68f',
                      }}
                    >
                      {c.status === 'released' ? tt('Released', '已发放') : c.status === 'flagged' ? tt('⚠ Flagged — cap reached', '⚠ 已标记 — 已达上限') : tt('Pending', '待发放')}
                    </span>
                  </td>
                  <td className="p-2 no-print">
                    <div className="flex gap-2">
                      {c.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleRelease([c.id])}
                          disabled={releasing || !isBjoechia}
                          title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                          className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                        >
                          {tt('Release', '发放')}
                        </button>
                      )}
                      {(c.status === 'pending' || c.status === 'flagged') && (
                        <button
                          type="button"
                          onClick={() => handleExclude(c.id)}
                          disabled={excludingId === c.id}
                          title={tt('Exclude this purchase from all future Direct Referral calculations', '将此笔购买从未来所有直推计算中排除')}
                          className="px-3 py-1 rounded font-medium text-body-xs border disabled:opacity-50"
                          style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                        >
                          {excludingId === c.id ? tt('Excluding...', '排除中...') : tt('Exclude', '排除')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
    </div>
    </div>
  );
}
